import {
  Button,
  cn,
  FormControl,
  Input,
  Plus,
  Trash,
} from "@gearbox-protocol/deploy-tools-ui";
import { useCallback, useState } from "react";
import type {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

interface EnvInputsProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  field: ControllerRenderProps<TFieldValues, TName>;
  className?: string;
  compact?: boolean;
  secret?: boolean;
}

const EnvInputs = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: EnvInputsProps<TFieldValues, TName>,
) => {
  const { field, className, compact, secret } = props;
  const [newEnv, setNewEnv] = useState({ key: "", value: "" });

  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: field.name,
  });

  const handleAddNewEnv = useCallback(() => {
    append(newEnv as any);
    setNewEnv({ key: "", value: "" });
  }, [append, newEnv]);

  const keyClassName = compact ? "h-8 w-32" : "h-8 w-64";
  const valueClassName = compact ? "h-8 w-48" : "h-8 w-96";
  // browsers key autofill history by the DOM name, so it must not contain the row index
  const keyName = `${field.name}-key`;
  const valueName = `${field.name}-value`;
  const valueAutoComplete = secret ? "off" : "on";

  return (
    <div className={cn("flex flex-col gap-y-1", className)}>
      {fields.map((item, index) => (
        <div key={item.id} className="flex flex-row items-center gap-2">
          <Controller
            control={control}
            name={`${field.name}.${index}.key`}
            render={({ field: f }) => (
              <FormControl>
                <Input
                  {...f}
                  value={f.value ?? ""}
                  name={keyName}
                  autoComplete="on"
                  placeholder="key"
                  className={keyClassName}
                />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name={`${field.name}.${index}.value`}
            render={({ field: f }) => (
              <FormControl>
                <Input
                  {...f}
                  value={f.value ?? ""}
                  name={valueName}
                  autoComplete={valueAutoComplete}
                  placeholder="value"
                  className={valueClassName}
                />
              </FormControl>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => remove(index)}
          >
            <Trash />
          </Button>
        </div>
      ))}

      <div className="flex flex-row items-center gap-2">
        <FormControl>
          <Input
            name={keyName}
            autoComplete="on"
            placeholder="key"
            className={keyClassName}
            value={newEnv.key}
            onChange={e => setNewEnv(old => ({ ...old, key: e.target.value }))}
          />
        </FormControl>
        <FormControl>
          <Input
            name={valueName}
            autoComplete={valueAutoComplete}
            placeholder="value"
            className={valueClassName}
            value={newEnv.value}
            onChange={e =>
              setNewEnv(old => ({ ...old, value: e.target.value }))
            }
          />
        </FormControl>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleAddNewEnv}
        >
          <Plus />
        </Button>
      </div>
    </div>
  );
};

export default EnvInputs;
